import AlertBanner from 'ext-ui/components/AlertBanner/AlertBanner'
import Button from 'ext-ui/components/Button/Button'
import Tooltip from 'ext-ui/components/Tooltip/Tooltip'
import TooltipIcon from 'ext-ui/components/TooltipIcon/TooltipIcon'
import CardsNewIcon from 'ext-ui/svg/icon/CardsNew'
import MagicalAIIcon from 'ext-ui/svg/icon/MagicalAIIcon'
import ThumbsDownIcon from 'ext-ui/svg/icon/ThumbsDownIcon'
import ThumbsUpIcon from 'ext-ui/svg/icon/ThumbsUpIcon'
import React, { useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import { createSetIsToastOpen } from 'src/app/Toast/redux/toast.actions'
import NormalizedMerchantFeedbackModal from 'src/component-modal/NormalizedMerchantFeedbackModal/NormalizedMerchantFeedbackModal'
import {
  useAIPolicyAssistantEnabled,
  useQueryExpensePolicy,
} from 'src/components/ExpensePolicyUploadBox/expensePolicy.queries'
import SvgMerchantPending from 'src/components/Svgs/SvgMerchantPending'
import Tag from 'src/components/Tag/Tag'
import TextAvatar from 'src/components/TextAvatar/TextAvatar'
import UserAvatar from 'src/components/UserAvatar/UserAvatar'
import { AvatarSizes } from 'src/enums'
import MerchantIcon from 'src/feature/MerchantIcon/MerchantIcon'
import { dateFormatString } from 'src/localization/Dte'
import { getLocaleInfo } from 'src/localization/locales'
import { useTranslate } from 'src/localization/Trns'
import {
  ConnectedPlatformStatus,
  TransactionDetailModel,
} from 'src/models/transaction.models'
import { UserListModel } from 'src/models/user.models'
import { useQueryOwnUser } from 'src/queries/users.queries'
import { selectNullSafeLocaleFromState } from 'src/redux/selectors/auth.selectors'
import { selectFeatureFlaggerFromState } from 'src/redux/selectors/data/featureFlagger.selectors'
import {
  selectIsBetaOrgNormalizedMerchant,
  selectUserExplicitOrganizationListModelFromState,
} from 'src/redux/selectors/data/organizations.selectors'
import { selectActiveOrgIntegration } from 'src/redux/selectors/data/orgIntegrations.selectors'
import { selectExpensePolicyAIWhitelistFromRemoteConfigState } from 'src/redux/selectors/data/remoteConfig.selectors'

import style, { iconStyle } from './TransactionSummarySection.style'

interface TransactionSummarySectionProps {
  transaction: TransactionDetailModel
  cardUser: UserListModel
}

const TransactionSummarySection: React.FC<TransactionSummarySectionProps> = ({
  transaction,
  cardUser,
}): React.ReactElement => {
  const translate = useTranslate()
  const dispatch = useDispatch()
  const organization = useSelector(
    selectUserExplicitOrganizationListModelFromState,
  )
  const { analytics } = useAnalyticsContext()
  const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false)
  const [hasFeedbackBeenProvided, setHasFeedbackBeenProvided] = useState(false)
  const locale = useSelector(selectNullSafeLocaleFromState)
  const enableNormalizedMerchant = useSelector(
    selectIsBetaOrgNormalizedMerchant,
  )
  const merchantImageSrc = transaction.merchantImageSrc('medium')
  const { data: user } = useQueryOwnUser({})
  const activeIntegration = useSelector(selectActiveOrgIntegration)
  const syncedToERP = transaction.connectedPlatforms?.find(
    (c) =>
      c.platform === activeIntegration?.connectedPlatform &&
      c.syncStatus === ConnectedPlatformStatus.Success,
  )
  const aiPolicyAssistantEnabled = useAIPolicyAssistantEnabled()
  const { show_expense_policy_ai: showExpensePolicyAIFF } = useSelector(
    selectFeatureFlaggerFromState,
  )
  const expensePolicyAIWhitelist = useSelector(
    selectExpensePolicyAIWhitelistFromRemoteConfigState,
  )
  const showExpensePolicyAI =
    showExpensePolicyAIFF ||
    expensePolicyAIWhitelist?.includes(user?.organizationId || '')

  const { data: policy } = useQueryExpensePolicy({
    orgId: organization?.id,
    enabled: aiPolicyAssistantEnabled,
  })

  const handlePositiveFeedback = () => {
    analytics?.track('transaction detail - normalized merchant - positive', {
      transactionId: transaction.id,
      normalizedMerchantName:
        transaction.normalizedMerchant?.normalizedMerchantName ?? '',
      originalMerchantName: transaction.merchantName ?? '',
    })

    setHasFeedbackBeenProvided(true)

    dispatch(
      createSetIsToastOpen(true, {
        toastMessage: <span>{translate('Thank you for your feedback!')}</span>,
      }),
    )
  }

  const handleNegativeFeedback = () => {
    setIsFeedbackModalOpen(true)
  }

  const handleFeedbackSubmit = (feedback: string) => {
    analytics?.track('transaction detail - normalized merchant - negative', {
      transactionId: transaction.id,
      normalizedMerchantName:
        transaction.normalizedMerchant?.normalizedMerchantName ?? '',
      originalMerchantName: transaction.merchantName ?? '',
      userFeedback: feedback,
    })

    setHasFeedbackBeenProvided(true)

    dispatch(
      createSetIsToastOpen(true, {
        toastMessage: <span>{translate('Thank you for your feedback!')}</span>,
      }),
    )
  }

  const insights = useMemo(
    () =>
      transaction.transactionAiInsights?.find(
        (insight) => insight.insightType === 'POLICY_AGENT',
      ),
    [transaction.transactionAiInsights],
  )

  return (
    <div css={style} className={'transactionSummarySection'}>
      <div className={'flex-column gap-4'}>
        <div
          className={'flex-row gap-2 align-baseline font-h1 text-primary'}
          data-testid="transaction-amount"
        >
          {transaction.clearingOrAuthLabel(true)}
          {transaction.currency !== getLocaleInfo(locale).currency && (
            <div className={'font-regular text-secondary'}>
              {transaction.currency}
            </div>
          )}
        </div>
        <div className={'details'}>
          <span className={'flex-row gap-1 align-center'}>
            <CardsNewIcon />
            <span
              className={'transaction-card-name'}
              data-testid="transaction-card-name"
            >
              {transaction.vcnDisplayName}
            </span>
            <Tag fontSize={10} text={`${transaction.vcnLast4}`} />
          </span>
          {transaction.clearedAt && (
            <span data-testid="transaction-cleared-date">
              {dateFormatString(transaction.clearedAt, 'l')}
            </span>
          )}
          {transaction.status !== 'DECLINED' && (
            <span data-testid="transaction-status">
              {transaction.pseudoStatusLabelNew}
            </span>
          )}
          {transaction.status === 'DECLINED' && transaction.declineReason && (
            <span className={'flex-row'} data-testid="transaction-status">
              <Tooltip tooltipContent={transaction.declineReason}>
                <span className={'decline-reason'}>
                  {transaction.declineReason}
                </span>
              </Tooltip>
            </span>
          )}
          {user?.hasOrgBookkeeperPermissions && syncedToERP && (
            <TooltipIcon
              icon={
                <img css={iconStyle} src={activeIntegration?.logo} alt={''} />
              }
            >
              {translate('Synced to ${orgLvlIntegration}', {
                orgLvlIntegration: activeIntegration?.platformName || '',
              })}
            </TooltipIcon>
          )}
        </div>
      </div>
      <div className={'timeline'}>
        <div className={'timeline-event'}>
          {enableNormalizedMerchant ? (
            <MerchantIcon
              size={36}
              merchantImgSrc={merchantImageSrc}
              mccGroupKey={transaction.mccGroupKey ?? 'OTHER'}
            />
          ) : (
            <TextAvatar
              size={AvatarSizes.MediumLarge}
              text={transaction.merchantName?.[0]}
              defaultIcon={<SvgMerchantPending />}
            />
          )}
          <div className={'flex-column'}>
            <div
              className={'font-h3 text-primary flex-row gap-1 align-center'}
              data-testid="merchant-name"
            >
              {enableNormalizedMerchant
                ? transaction.normalizedMerchantNameOrUnknown
                : transaction.merchantNameOrUnknown}
              {enableNormalizedMerchant && transaction.normalizedMerchant && (
                <>
                  <TooltipIcon>
                    {translate('Original Merchant: ${merchantName}', {
                      merchantName:
                        transaction.merchantName ??
                        translate('Unknown Merchant'),
                    })}
                  </TooltipIcon>
                  {!hasFeedbackBeenProvided && (
                    <>
                      <Tooltip
                        tooltipContent={translate(
                          'This merchant name is correct',
                        )}
                      >
                        <Button
                          variant="toolbar"
                          dropShadow={false}
                          onClick={handlePositiveFeedback}
                          testId="normalized-merchant-thumbs-up"
                          aria-label="Thumbs up"
                          className="feedback-button positive"
                        >
                          <ThumbsUpIcon />
                        </Button>
                      </Tooltip>
                      <Tooltip
                        tooltipContent={translate(
                          'This merchant name is incorrect',
                        )}
                      >
                        <Button
                          variant="toolbar"
                          dropShadow={false}
                          onClick={handleNegativeFeedback}
                          testId="normalized-merchant-thumbs-down"
                          aria-label="Thumbs down"
                          className="feedback-button negative"
                        >
                          <ThumbsDownIcon />
                        </Button>
                      </Tooltip>
                    </>
                  )}
                </>
              )}
            </div>
            <div className={'font-md text-secondary'} data-testid="mcc-group">
              {transaction.mccGroup}
            </div>
          </div>
        </div>
        <div className={'timeline-event'}>
          <UserAvatar user={cardUser} size={AvatarSizes.MediumLarge} />
          <div className={'flex-column'}>
            <div className={'font-h3 text-primary'}>
              <span data-testid="card-user-name">
                {transaction.recipientName}
              </span>
            </div>
            {transaction.date && (
              <div
                className={'font-md text-secondary'}
                data-testid="transaction-auth-date-time"
              >
                {dateFormatString(transaction.date, 'l, LT')}
              </div>
            )}
          </div>
        </div>
      </div>
      {showExpensePolicyAI &&
        aiPolicyAssistantEnabled &&
        insights?.insightText && (
          <AlertBanner
            className={'expense-policy-ai-banner'}
            data-testid="rejection-notice"
            variant={'new'}
            title={translate('Policy insights')}
            icon={<MagicalAIIcon />}
            isCollapsible={true}
          >
            {insights?.insightText}
            {policy && (
              <a
                href={policy.urls.original}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={'policyDetailsLink'}
              >
                <Button
                  variant={'text'}
                  testId={'policyDetailsButton'}
                  aria-label={translate('See policy details')}
                >
                  {translate('See policy details')}
                </Button>
              </a>
            )}
          </AlertBanner>
        )}

      <NormalizedMerchantFeedbackModal
        isOpen={isFeedbackModalOpen}
        onClose={() => setIsFeedbackModalOpen(false)}
        onSubmit={handleFeedbackSubmit}
        normalizedMerchantName={
          transaction.normalizedMerchant?.normalizedMerchantName ?? ''
        }
        originalMerchantName={transaction.merchantName ?? ''}
      />
    </div>
  )
}

export default TransactionSummarySection
